import { useCallback, useState } from 'react'
import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { usePageTitle } from '@/hooks/usePageTitle'
import { CITY_NAME } from '@/meta'
import DocumentationViewer, { DEFAULT_VIEWER_PREFERENCES, type ViewerPreferences } from './DocumentationViewer'
import DropScreen, { DropHighlight } from './DropScreen'
import { useArchive } from './useArchive'
import { useFileDrop } from './useFileDrop'

const DocumentationPage = () => {
  const { t } = useTranslation('documentation')
  usePageTitle(`${t('title')} | ${CITY_NAME}`)
  const { archive, dropOpen, status, openFirst, showDrop, hideDrop } = useArchive()
  const [preferences, setPreferences] = useState<ViewerPreferences>(DEFAULT_VIEWER_PREFERENCES)
  const updatePreferences = useCallback(
    (patch: Partial<ViewerPreferences>) => setPreferences(previous => ({ ...previous, ...patch })),
    []
  )

  const { hot, handlers } = useFileDrop(files => {
    showDrop()
    openFirst(files)
  })

  return (
    <Box
      h="calc(100dvh - var(--app-shell-header-height, 60px))"
      pos="relative"
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      {...handlers}
    >
      {archive ? (
        <DocumentationViewer
          key={archive.id}
          archive={archive}
          active={!dropOpen}
          preferences={preferences}
          onPreferences={updatePreferences}
          onLoadArchive={showDrop}
        />
      ) : null}
      {dropOpen ? (
        <DropScreen
          hot={hot}
          summary={archive ? t('indexed', { name: archive.file.name, count: archive.imageCount }) : null}
          status={status.text}
          statusError={status.error}
          onFiles={openFirst}
          onCancel={archive ? hideDrop : undefined}
        />
      ) : null}
      {hot && !dropOpen ? <DropHighlight /> : null}
    </Box>
  )
}

export default DocumentationPage
